import React, { useEffect, useState, memo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle } from 'lucide-react';
import { processOAuthCallback } from '../api/services';

import styles from './styles/OAuthCallback.module.css';

const OAuthCallback = () => {
    const [status, setStatus] = useState('processing');
    const [error, setError] = useState(null);
    const [isAgentFlow, setIsAgentFlow] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const code = queryParams.get('code');
        const state = queryParams.get('state');
        const error = queryParams.get('error');

        if (error) {
            setStatus('error');
            setError(error);
            return;
        }

        if (!code || !state) {
            setStatus('error');
            setError('Missing required parameters');
            return;
        }

        // Process OAuth callback
        const handleOAuthCallback = async () => {
            try {
                // Check if this is an agent OAuth flow
                const agentId = localStorage.getItem('oauthAgentId');
                const serviceId = localStorage.getItem('oauthServiceId');

                if (agentId && serviceId) {
                    // Agent OAuth flow - for agents, backend handles everything via existing callback
                    setIsAgentFlow(true);
                    await processOAuthCallback(location.search);

                    // Clean up localStorage
                    localStorage.removeItem('oauthAgentId');
                    localStorage.removeItem('oauthServiceId');

                    setStatus('success');

                    // Redirect back to agent settings after a delay
                    setTimeout(() => {
                        // navigate(`/agents/${agentId}/settings?tab=tools`);
                        navigate('/agents');
                    }, 2000);
                } else {
                    // Regular user OAuth flow
                    await processOAuthCallback(location.search);

                    setStatus('success');

                    // Redirect back to the service page after a delay
                    setTimeout(() => {
                        navigate('/store');
                    }, 2000);
                }
            } catch (error) {
                console.error('Error processing OAuth callback:', error);
                setStatus('error');
                setError(error.message || 'Failed to process OAuth callback');
            }
        };

        handleOAuthCallback();
    }, [location, navigate]);

    return (
        <div className={styles.container}>
            <div className={styles.card}>
                {status === 'processing' && (
                    <div className={styles.processing}>
                        <div className={styles.spinner}></div>
                        <p>
                            {isAgentFlow
                                ? 'Processing agent OAuth authorization...'
                                : 'Processing OAuth authorization...'
                            }
                        </p>
                    </div>
                )}

                {status === 'success' && (
                    <div className={styles.success}>
                        <CheckCircle size={48} color="#16a34a" />
                        <h2>Authorization Successful</h2>
                        <p>
                            {isAgentFlow
                                ? 'You have successfully authorized the agent.'
                                : 'You have successfully authorized the application.'
                            }
                        </p>
                        <p className={styles.redirecting}>
                            {isAgentFlow
                                ? 'Redirecting you back to agent settings...'
                                : 'Redirecting you back to the service...'
                            }
                        </p>
                    </div>
                )}

                {status === 'error' && (
                    <div className={styles.error}>
                        <XCircle size={48} color="#dc2626" />
                        <h2>Authorization Failed</h2>
                        <p>{error || 'An error occurred during authorization.'}</p>
                        <button
                            className={styles.returnButton}
                            onClick={() => navigate(isAgentFlow ? '/agents' : '/store')}
                        >
                            {isAgentFlow ? 'Return to Agents' : 'Return to Services'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default memo(OAuthCallback);
